import React, { useCallback, useEffect, useState } from 'react';
import { View, ViewStyle } from 'react-native';
import Modal from '@/shared/components/Modal';
import styled from '@emotion/native';
import { useShallow } from 'zustand/react/shallow';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import useRouteThemeStore from '@/stores/useRouteThemeStore';
import { green, sand, white } from '@/shared/styles/colors';
import { useMutation, useQuery } from '@apollo/client';
import { GET_DEFAULT_PAYMENT_METHOD, GET_PRICING } from '@/shared/queries';
import Button from '@/shared/components/Button';
import SentryService from '@/services/sentry/Sentry.service';
import { BOX_SHADOW_STYLE } from '@/shared/styles/constants';
import InstantStylingModalProgress from '@/components/instantStyling/components/InstantStylingModalProgress';
import { Portal } from 'react-native-paper';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import {
  MutationClientAcceptMatchArgs,
  MutationClientCancelRequestArgs,
  MutationClientDeclineMatchArgs,
  MutationCreateConsultationArgs,
  MutationCreateInstantStylingRequestArgs,
  MutationCreatePaymentIntentArgs,
  MutationResult,
  QueryResult,
} from '@/shared/types/generated';
import ScrollableView from '@/shared/components/ScrollableView';
import useExploreStore from '@/stores/useExploreStore';
import ClientInstantCallPreferences from './ClientInstantCallPreferences';
import ClientInstantCallMorePreferences from './ClientInstantCallMorePreferences';
import ClientInstantCallReview from './ClientInstantCallReview';
import ClientFindingMatch from './ClientFindingMatch';
import ClientMatchFound from './ClientMatchFound';
import {
  getDurationFromInstantEventType,
  getHourlyRateFromPricingRequest,
} from '../helpers';
import { getCurrentTimeZone, getDropdownOptionLabel } from '@/shared/utils';
import { PRICING_TIER_OPTIONS } from '@/shared/constants/instantCall';
import {
  CLIENT_ACCEPT_MATCH,
  CLIENT_CANCEL_REQUEST,
  CLIENT_DECLINE_MATCH,
  CREATE_CONSULTATION,
  CREATE_INSTANT_STYLING_REQUEST,
  CREATE_PAYMENT_INTENT,
} from '@/shared/mutations';
import useStore from '@/stores/useStore';
import { BaseText } from '@/shared/components/StyledText';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import ModalWrapper from '@/shared/styles/components/ModalWrapper';

// Using 3 for progress bar even though there are more screens
const TOTAL_PROGRESS_STEPS = 3;

const PREFERENCES_STEP = 0;
const MORE_PREFERENCES_STEP = 1;
const REVIEW_STEP = 2;
const FINDING_MATCH_STEP = 3;
const MATCH_FOUND_STEP = 4;

const MAX_SEARCH_ATTEMPTS = 3;

interface Props {
  handleJoinSession: () => void;
}

const ClientInstantStylingModal = ({ handleJoinSession }: Props) => {
  const router = useRouter();
  const scrollToTop = useScrollToTop();

  const setSnackbarState = useStore((state) => state.setSnackbarState);

  const {
    isInstantStylingModalVisible,
    setIsInstantStylingModalVisible,
    requestForm,
    setRequestForm,
    matchedStylist,
    matchStatus,
    setMatchStatus,
    setMatchedStylist,
    resetMatchStatus,
    resetMatchStatusKeepRequestId,
    videoCallStarted,
    resetStore,
  } = useInstantStylingStore(
    useShallow((state) => ({
      isInstantStylingModalVisible: state.isInstantStylingModalVisible,
      setIsInstantStylingModalVisible: state.setIsInstantStylingModalVisible,
      requestForm: state.requestForm,
      setRequestForm: state.setRequestForm,
      matchedStylist: state.matchedStylist,
      matchStatus: state.matchStatus,
      setMatchStatus: state.setMatchStatus,
      setMatchedStylist: state.setMatchedStylist,
      resetMatchStatus: state.resetMatchStatus,
      resetMatchStatusKeepRequestId: state.resetMatchStatusKeepRequestId,
      videoCallStarted: state.videoCallStarted,
      resetStore: state.resetStore,
    })),
  );
  const { setBooking, resetBooking } = useExploreStore(
    useShallow((state) => ({
      setBooking: state.setBooking,
      resetBooking: state.resetBooking,
    })),
  );

  const { setOverride, clearOverride } = useRouteThemeStore(
    useShallow((state) => ({
      setOverride: state.setOverride,
      clearOverride: state.clearOverride,
    })),
  );

  const [progress, setProgress] = useState(0);
  const [hideProgressBar, setHideProgressBar] = useState(false);
  const [hideContinueButton, setHideContinueButton] = useState(false);
  const [isStylistReady, setIsStylistReady] = useState(false);
  const [isContinueLoading, setIsContinueLoading] = useState(false);

  const { data: pricingData } = useQuery<QueryResult<'pricing'>>(GET_PRICING, {
    onError: (error) => {
      SentryService.captureError('Error fetching pricing data', error);
    },
  });

  const { data: defaultPaymentData } = useQuery<
    QueryResult<'getDefaultPaymentMethod'>
  >(GET_DEFAULT_PAYMENT_METHOD);

  const [createInstantStylingRequest] = useMutation<
    MutationResult<'createInstantStylingRequest'>,
    MutationCreateInstantStylingRequestArgs
  >(CREATE_INSTANT_STYLING_REQUEST);

  const [createPaymentIntent] = useMutation<
    MutationResult<'createPaymentIntent'>,
    MutationCreatePaymentIntentArgs
  >(CREATE_PAYMENT_INTENT);

  const [createConsultation] = useMutation<
    MutationResult<'createConsultation'>,
    MutationCreateConsultationArgs
  >(CREATE_CONSULTATION);

  const [clientAcceptMatch] = useMutation<
    MutationResult<'clientAcceptMatch'>,
    MutationClientAcceptMatchArgs
  >(CLIENT_ACCEPT_MATCH);

  const [clientCancelRequest] = useMutation<
    MutationResult<'clientCancelRequest'>,
    MutationClientCancelRequestArgs
  >(CLIENT_CANCEL_REQUEST);

  const [clientDeclineMatch] = useMutation<
    MutationResult<'clientDeclineMatch'>,
    MutationClientDeclineMatchArgs
  >(CLIENT_DECLINE_MATCH);

  const isContinueDisabled =
    progress === REVIEW_STEP &&
    !defaultPaymentData?.getDefaultPaymentMethod?.paymentMethodId;
  const isFindingMatch = progress === FINDING_MATCH_STEP;
  const isWaitingForStylist = matchStatus?.clientAccepted && !isStylistReady;

  const showBackButton = progress > 0 && progress < FINDING_MATCH_STEP;
  const showSearchAgainLink =
    progress === MATCH_FOUND_STEP && !isWaitingForStylist && !isStylistReady;
  const maxRetriesReached =
    requestForm.declinedStylistIds.length >= MAX_SEARCH_ATTEMPTS - 1;
  const showJoinConsultationButton = Boolean(videoCallStarted?.sessionName);

  const priceTierName = getDropdownOptionLabel(
    requestForm.pricingTier,
    PRICING_TIER_OPTIONS,
  );

  const hourlyRate = getHourlyRateFromPricingRequest(
    requestForm.pricingTier,
    pricingData?.pricing ?? [],
  );

  const handleClientDeclineMatch = useCallback(async () => {
    try {
      if (!matchStatus?.requestId) {
        throw new Error('Match status request ID is missing');
      }

      await clientDeclineMatch({
        variables: {
          input: { requestId: matchStatus.requestId },
        },
      });
    } catch (error) {
      SentryService.captureError('Error in client declining match', error);
      setSnackbarState({
        open: true,
        message: 'Error declining match. Please try again.',
        severity: 'error',
      });
    }
  }, [clientDeclineMatch, matchStatus?.requestId, setSnackbarState]);

  const handleClientCancelRequest = useCallback(async () => {
    try {
      if (!matchStatus?.requestId) {
        throw new Error('Match status request ID is missing');
      }

      await clientCancelRequest({
        variables: {
          input: { requestId: matchStatus.requestId },
        },
      });
    } catch (error) {
      SentryService.captureError('Error in client canceling request', error);
      setSnackbarState({
        open: true,
        message: 'Error canceling request. Please try again.',
        severity: 'error',
      });
    }
  }, [clientCancelRequest, matchStatus?.requestId, setSnackbarState]);

  const goBackToFindingMatch = useCallback(() => {
    if (matchedStylist) {
      setRequestForm({
        declinedStylistIds: [
          ...requestForm.declinedStylistIds,
          matchedStylist.id,
        ],
      });
    }

    setProgress(FINDING_MATCH_STEP);
    resetMatchStatusKeepRequestId();
    setMatchedStylist(null);
    scrollToTop();
  }, [
    matchedStylist,
    requestForm.declinedStylistIds,
    resetMatchStatusKeepRequestId,
    scrollToTop,
    setMatchedStylist,
    setRequestForm,
  ]);

  useEffect(() => {
    if (matchStatus?.stylistAccepted && videoCallStarted?.sessionName) {
      setIsStylistReady(true);
    }
  }, [matchStatus?.stylistAccepted, videoCallStarted?.sessionName]);

  useEffect(() => {
    if (matchStatus?.stylistDeclined) {
      goBackToFindingMatch();
    }
  }, [goBackToFindingMatch, matchStatus?.stylistDeclined]);

  // Override status bar to dark content when modal is visible
  useEffect(() => {
    if (isInstantStylingModalVisible) {
      setOverride({
        statusBarStyle: 'dark-content',
        topSafeAreaBackground: sand,
      });
    } else {
      clearOverride();
    }

    return () => {
      clearOverride();
    };
  }, [isInstantStylingModalVisible, setOverride, clearOverride]);

  const onPressBack = () => {
    setProgress((prev) => prev - 1);
    scrollToTop();
  };

  const onPressSearchAgain = async () => {
    if (maxRetriesReached) {
      router.push(ROUTES.EXPLORE.path);
      onDismiss();
      return;
    }

    await handleClientDeclineMatch();

    if (matchedStylist) {
      setRequestForm({
        declinedStylistIds: [
          ...requestForm.declinedStylistIds,
          matchedStylist.id,
        ],
      });
    }

    setProgress(PREFERENCES_STEP);
    resetMatchStatus();
    scrollToTop();
  };

  const onTimerExpire = async () => {
    await handleClientDeclineMatch();
    goBackToFindingMatch();
  };

  const onDismiss = async () => {
    if (progress === FINDING_MATCH_STEP) {
      await handleClientCancelRequest();
    }

    // Only decline if we're in MATCH_FOUND_STEP and the client hasn't accepted
    // AND there's no active video session (prevents declining when joining call)
    if (
      progress === MATCH_FOUND_STEP &&
      !matchStatus?.clientAccepted &&
      !videoCallStarted?.sessionName &&
      !isStylistReady
    ) {
      await handleClientDeclineMatch();
    }

    setIsInstantStylingModalVisible(false);
    setProgress(0);
    setHideProgressBar(false);
    setHideContinueButton(false);
    setIsStylistReady(false);
    resetStore();
    resetBooking();
  };

  const getContinueButtonCopy = () => {
    switch (progress) {
      case PREFERENCES_STEP:
        return 'Continue';
      case MORE_PREFERENCES_STEP:
        return 'Review Preferences';
      case REVIEW_STEP:
        return 'Request Match';
      case FINDING_MATCH_STEP:
        return 'Cancel Request';
      case MATCH_FOUND_STEP: {
        if (showJoinConsultationButton) {
          return 'Join Consultation';
        }
        return 'Accept & Notify Match';
      }
      default:
        return 'Continue';
    }
  };

  const onContinuePress = async () => {
    switch (progress) {
      case MORE_PREFERENCES_STEP: {
        const sessionDuration = getDurationFromInstantEventType(
          requestForm.duration,
        );
        const now = new Date();

        setBooking({
          sessionDuration,
          selectedDate: now,
          selectedTime: now.getTime(),
          reason: requestForm.reason,
          additionalDetails: requestForm.details,
        });
        break;
      }
      case REVIEW_STEP: {
        try {
          setIsContinueLoading(true);

          const instantStylingRequest = await createInstantStylingRequest({
            variables: {
              input: {
                pricingTier: requestForm.pricingTier,
                duration: requestForm.duration,
                reason: requestForm.reason,
                gender: requestForm.gender,
                age: requestForm.age,
                style: requestForm.style,
                details: requestForm.details,
              },
            },
          });

          if (!instantStylingRequest.data?.createInstantStylingRequest?.id) {
            throw new Error('Missing instant styling request ID');
          }

          setMatchStatus({
            requestId:
              instantStylingRequest.data.createInstantStylingRequest.id,
          });
          break;
        } catch (error) {
          SentryService.captureError(
            'Error in client creating instant styling request',
            error,
          );
          setSnackbarState({
            open: true,
            message:
              'Error creating instant styling request. Please try again.',
            severity: 'error',
          });
          return;
        } finally {
          setIsContinueLoading(false);
        }
      }
      case FINDING_MATCH_STEP: {
        // Client canceled the request - Reset and go to first step
        setIsContinueLoading(true);
        await handleClientCancelRequest();
        resetMatchStatus();
        setIsContinueLoading(false);

        setProgress(PREFERENCES_STEP);
        scrollToTop();
        return;
      }
      case MATCH_FOUND_STEP: {
        if (isStylistReady) {
          setIsInstantStylingModalVisible(false);
          handleJoinSession();
          return;
        }

        try {
          setIsContinueLoading(true);

          if (!matchStatus?.stylistUserId || !matchedStylist?.id) {
            throw new Error('Match found stylist user ID is missing');
          }

          const paymentIntent = await createPaymentIntent({
            variables: {
              input: {
                stylistId: matchedStylist.id,
                amount: hourlyRate,
                ...(requestForm.discountCode && {
                  discountCode: requestForm.discountCode,
                }),
              },
            },
          });

          if (!paymentIntent.data?.createPaymentIntent?.id) {
            throw new Error('Missing payment intent ID');
          }

          const consultation = await createConsultation({
            variables: {
              input: {
                stylistId: matchedStylist.id,
                paymentId: paymentIntent.data.createPaymentIntent.id,
                isInstant: true,
                startTime: new Date(),
                estimatedDuration: getDurationFromInstantEventType(
                  requestForm.duration,
                ),
                clientNotes: requestForm.details,
                reason: requestForm.reason,
                style: requestForm.style,
                clientTimezone: getCurrentTimeZone(),
              },
            },
          });

          if (!consultation.data?.createConsultation?.id) {
            throw new Error('Missing consultation ID');
          }

          await clientAcceptMatch({
            variables: {
              input: {
                requestId: matchStatus.requestId,
                stylistUserId: matchStatus.stylistUserId,
                paymentId: paymentIntent.data.createPaymentIntent.id,
                consultationId: consultation.data.createConsultation.id,
              },
            },
          });

          setMatchStatus({
            clientAccepted: true,
          });
          return;
        } catch (error) {
          // The promo code can expire or be switched off between review and
          // accepting. Drop it so the client explicitly accepts again at full
          // price instead of retrying the same rejected code.
          const isPromoCodeError =
            Boolean(requestForm.discountCode) &&
            /discount code/i.test((error as Error)?.message ?? '');
          if (isPromoCodeError) {
            setRequestForm({ discountCode: null, discountPercentage: null });
            setSnackbarState({
              open: true,
              message:
                'Your promo code is no longer valid and was removed. Tap accept again to continue at full price.',
              severity: 'error',
            });
            return;
          }
          SentryService.captureError('Error in client accepting match', error);
          setSnackbarState({
            open: true,
            message: 'Error accepting match. Please try again.',
            severity: 'error',
          });
          return;
        } finally {
          setIsContinueLoading(false);
        }
      }
    }

    setProgress((prev) => prev + 1);
    scrollToTop();
  };

  const renderStep = () => {
    switch (progress) {
      case PREFERENCES_STEP:
        return <ClientInstantCallPreferences />;
      case MORE_PREFERENCES_STEP:
        return <ClientInstantCallMorePreferences />;
      case REVIEW_STEP:
        return (
          <ClientInstantCallReview
            priceTierName={priceTierName}
            hourlyRate={hourlyRate}
            onDismissModal={onDismiss}
            discountCode={requestForm.discountCode}
            discountPercentage={requestForm.discountPercentage}
            onDiscountCodeChange={(discountCode, discountPercentage) =>
              setRequestForm({
                discountCode,
                discountPercentage: discountCode
                  ? (discountPercentage ?? null)
                  : null,
              })
            }
          />
        );
      case FINDING_MATCH_STEP:
        return (
          <ClientFindingMatch
            priceTierName={priceTierName}
            hourlyRate={hourlyRate}
            setProgress={setProgress}
            setHideProgressBar={setHideProgressBar}
            setHideContinueButton={setHideContinueButton}
            onDismiss={onDismiss}
          />
        );
      case MATCH_FOUND_STEP:
        return <ClientMatchFound onTimerExpire={onTimerExpire} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      isVisible={isInstantStylingModalVisible}
      setIsVisible={setIsInstantStylingModalVisible}
      onDismiss={onDismiss}
      onRequestClose={onDismiss}
      mobileBackgroundColor={sand}
    >
      <Portal.Host>
        <ModalWrapper style={{ width: '100%', flex: 1, backgroundColor: sand }}>
          <TopButtonRow>
            {showBackButton ? (
              <BackButton onPress={onPressBack}>
                <BackButtonImage
                  source={require('@/assets/images/BackWithCircle.png')}
                />
              </BackButton>
            ) : showSearchAgainLink ? (
              <SearchAgainText>
                Not a Fit?{' '}
                <SearchAgainLink onPress={onPressSearchAgain}>
                  {maxRetriesReached ? 'Schedule Session' : 'Search Again'}
                </SearchAgainLink>
              </SearchAgainText>
            ) : (
              <View style={{ height: 20, width: 50 }} />
            )}
            <CloseButton onPress={onDismiss}>
              <CloseIcon source={require('@/assets/images/CloseIcon.png')} />
            </CloseButton>
          </TopButtonRow>
          {!hideProgressBar && (
            <InstantStylingModalProgress
              progress={progress}
              totalSteps={TOTAL_PROGRESS_STEPS}
            />
          )}
          <ScrollableView
            style={{ width: '100%', height: '100%' }}
            contentContainerStyle={scrollViewContainerStyle}
          >
            <ContentContainer>{renderStep()}</ContentContainer>
          </ScrollableView>

          {!hideContinueButton && (
            <ContinueButtonContainer>
              {isWaitingForStylist ? (
                <OptionToJoinText>
                  {`Option to join will show here once\nmatched stylist is ready.`}
                </OptionToJoinText>
              ) : (
                <Button
                  text={getContinueButtonCopy()}
                  onPress={onContinuePress}
                  loading={isContinueLoading}
                  disabled={isContinueDisabled || isContinueLoading}
                  variant={isFindingMatch ? 'secondary' : 'primary'}
                  customStyle={isFindingMatch ? { backgroundColor: white } : {}}
                />
              )}
            </ContinueButtonContainer>
          )}
        </ModalWrapper>
      </Portal.Host>
    </Modal>
  );
};

export default ClientInstantStylingModal;

const scrollViewContainerStyle: ViewStyle = {
  flexGrow: 1,
  alignItems: 'center',
};

const ContentContainer = styled.View({
  flex: 1,
  paddingTop: 16,
  paddingBottom: 48,
  alignItems: 'center',
  width: '100%',
  backgroundColor: sand,
  borderRadius: 0,
  maxWidth: '100%',
  height: '100%',
});

const TopButtonRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: sand,
  paddingLeft: 16,
  paddingRight: 24,
});

const BackButton = styled.Pressable({
  padding: 8,
});

const BackButtonImage = styled.Image({
  width: 34,
  height: 34,
  resizeMode: 'contain',
  tintColor: green,
});

const SearchAgainText = styled(BaseText)({
  fontSize: 12,
  marginLeft: 8,
});

const SearchAgainLink = styled(BaseText)({
  fontSize: 12,
  fontWeight: 'bold',
  textDecorationLine: 'underline',
});

const CloseButton = styled.Pressable({
  justifyContent: 'center',
  alignItems: 'center',
  padding: 8,
});

const CloseIcon = styled.Image({
  width: 24,
  height: 24,
  resizeMode: 'contain',
  tintColor: green,
});

const ContinueButtonContainer = styled.View({
  height: 114,
  flexDirection: 'row',
  position: 'absolute',
  bottom: 0,
  width: '100%',
  justifyContent: 'center',
  backgroundColor: sand,
  paddingHorizontal: 32,
  paddingVertical: 20,
  ...BOX_SHADOW_STYLE,
});

const OptionToJoinText = styled(BaseText)({
  fontSize: 16,
  fontStyle: 'italic',
  letterSpacing: -0.16,
  textAlign: 'center',
});
